/** Strings in the player's language, from the single source in packages/assets. */
import { LANGUAGES, type Locale, STRINGS, type StringKey } from "@mistsweeper/assets";

export type { Locale, StringKey };

/** Whether a saved or asked-for value names a language the strings come in, draft or not. */
export function isLocale(value: unknown): value is Locale {
  return LANGUAGES.some((l) => l.code === value);
}

/**
 * The language a browser language tag asks for, among the given ones: Chinese by its script, so
 * zh-TW and zh-HK find zh-Hant and zh and zh-CN find zh-Hans, every other language by its
 * language alone. Null when none of them is the one asked for.
 */
export function matchLocale<T extends string>(tag: string, among: readonly T[]): T | null {
  let language = tag.toLowerCase().split(/[-_]/)[0] ?? "";
  try {
    const full = new Intl.Locale(tag).maximize();
    language = full.language === "zh" ? `zh-${full.script}` : full.language;
  } catch {
    // Not a tag Intl reads: its first part is the language.
  }
  return among.find((code) => code.toLowerCase() === language.toLowerCase()) ?? null;
}

/**
 * The language the browser asks for: the first of its preferred languages that a released
 * translation comes in (decision 0026), else English.
 */
export function detectLocale(): Locale {
  const langs =
    typeof navigator === "undefined" ? [] : (navigator.languages ?? [navigator.language]);
  const released = LANGUAGES.filter((l) => l.released).map((l) => l.code);
  for (const lang of langs) {
    const found = matchLocale(lang, released);
    if (found) return found;
  }
  return "en";
}

/** The language `?lang=` names, drafts included, for pictures and for trying a translation. */
export function askedLocale(): Locale | null {
  if (typeof location === "undefined") return null;
  const asked = new URLSearchParams(location.search).get("lang");
  return isLocale(asked) ? asked : null;
}

export function t(
  locale: Locale,
  key: StringKey,
  params: Record<string, string | number> = {},
): string {
  let s: string = STRINGS[locale][key];
  for (const [name, value] of Object.entries(params)) s = s.replaceAll(`{${name}}`, String(value));
  return s;
}

/**
 * Whether a count takes a pair's string for one, such as `over.streakDay`: 1, and in French 0 as
 * well, as its plural rule has it ("0 jour"). Languages without plurals write both alike.
 */
export function takesOne(locale: Locale, n: number): boolean {
  return n === 1 || (n === 0 && locale === "fr");
}

/** The locale a language's dates are written in. */
export function dateLocale(locale: Locale): string {
  return LANGUAGES.find((l) => l.code === locale)?.date ?? "en-GB";
}

/** "5 h 12 min" or "12 min". */
export function formatDuration(locale: Locale, ms: number): string {
  const minutes = Math.max(0, Math.ceil(ms / 60_000));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? t(locale, "time.hoursMinutes", { h, m }) : t(locale, "time.minutes", { m });
}
